import type {
  MotifFinContrat,
  ModeRemunerationElement,
  StatutContrat,
  TempsTravail,
  TypeTitreTransport,
} from '../types';

/**
 * Vocabulaire des contrats et de la paie, partage entre les ecrans
 * Personnel, Contrats et Paie. Hors composant : un fichier qui exporte a
 * la fois des composants et des constantes casse le rafraichissement a
 * chaud.
 */

export const STATUTS_CONTRAT: { valeur: StatutContrat; libelle: string }[] = [
  { valeur: 'animateur', libelle: 'Animateur' },
  { valeur: 'professeur', libelle: 'Professeur' },
  { valeur: 'administratif', libelle: 'Administratif' },
];

export function libelleStatutContrat(statut: StatutContrat): string {
  return STATUTS_CONTRAT.find((s) => s.valeur === statut)?.libelle ?? statut;
}

/**
 * TEMPS DE TRAVAIL (2026-10-02). Il decide de la nature de la paie :
 * un administratif a un salaire fixe, plein ou partiel ; un professeur ou
 * un animateur a temps plein aussi, et ses creneaux ne produisent plus
 * d'elements de paie ; a temps partiel il est paye au reel, sur ses cours.
 */
export const TEMPS_TRAVAIL: { valeur: TempsTravail; libelle: string }[] = [
  { valeur: 'plein', libelle: 'Temps plein' },
  { valeur: 'partiel', libelle: 'Temps partiel' },
];

export function libelleTempsTravail(temps: TempsTravail): string {
  return TEMPS_TRAVAIL.find((t) => t.valeur === temps)?.libelle ?? temps;
}

/** Heures hebdomadaires du temps plein, par statut (convention ECLAT ; 35 h legales). */
export const BASE_TEMPS_PLEIN: Record<StatutContrat, number> = {
  professeur: 24,
  animateur: 26,
  administratif: 35,
};

/** Le pendant de `natureRemuneration` du serveur : les deux doivent rester d'accord. */
export type NatureRemuneration = 'fixe_administratif' | 'fixe_cours' | 'reel';

export function natureRemuneration(statut: StatutContrat, temps: TempsTravail): NatureRemuneration {
  if (statut === 'administratif') return 'fixe_administratif';
  return temps === 'plein' ? 'fixe_cours' : 'reel';
}

/** Ce qui met fin a un contrat (2026-09-30). Le motif est une information RH. */
export const MOTIFS_FIN_CONTRAT: { valeur: MotifFinContrat; libelle: string }[] = [
  { valeur: 'fin_cdd', libelle: 'Fin de CDD' },
  { valeur: 'demission', libelle: 'Démission' },
  { valeur: 'rupture_conventionnelle', libelle: 'Rupture conventionnelle' },
];

export function libelleMotifFin(motif: MotifFinContrat): string {
  return MOTIFS_FIN_CONTRAT.find((m) => m.valeur === motif)?.libelle ?? motif;
}

/**
 * Mode de remuneration d'un ELEMENT de paie (descendu du contrat le
 * 2026-08-23) : permanent = regime general, declare au CEA ;
 * intermittent = cachet, declare via le GUSO.
 */
export const MODES_ELEMENT: { valeur: ModeRemunerationElement; libelle: string }[] = [
  { valeur: 'permanent', libelle: 'Permanent (Régime général)' },
  { valeur: 'intermittent', libelle: 'Intermittent (Cachet)' },
];

export function libelleModeElement(mode: ModeRemunerationElement): string {
  return MODES_ELEMENT.find((m) => m.valeur === mode)?.libelle ?? mode;
}

/**
 * Les types de titre de transport (2026-11-03 ; Imagine R le 2026-11-04),
 * dans l'ordre de la liste deroulante. La part remboursee n'est plus une
 * affaire d'ecran : le moteur la decide mois par mois selon le temps de
 * travail, et seule la paie l'affiche.
 */
export const TYPES_TITRE_TRANSPORT: { valeur: TypeTitreTransport; libelle: string }[] = [
  { valeur: 'navigo', libelle: 'Pass Navigo' },
  { valeur: 'unitaire', libelle: 'Titres unitaires' },
  { valeur: 'imagine_r', libelle: 'Forfait Imagine R' },
];

export function libelleTitreTransport(type: TypeTitreTransport | null | undefined): string {
  return TYPES_TITRE_TRANSPORT.find((t) => t.valeur === type)?.libelle ?? 'Pass Navigo';
}
